'use client'

import { ScrollingAnnouncementBar } from "@/components/dashboard/scrolling-announcement-bar"
import { GameInfoSection } from "@/components/dashboard/game-info-section"
import { NavigationMenu } from "@/components/dashboard/navigation-menu"
import { cn } from "@/lib/utils"
import { GenericTablePageContent } from "@/components/dashboard/generic-table-page-content"
import { renderListRowCells } from "@/components/dashboard/table-row-renderers"
import { useState, useEffect } from "react"
import { fetchSessions, apiService } from "@/lib/api"
import { Session } from "@/lib/api"

export default function ChoiceListPage() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [selectedGame, setSelectedGame] = useState("Booking - Choice")
  const [selectedSession, setSelectedSession] = useState<string>("")
  const [sessions, setSessions] = useState<Session[]>([])
  const [tableData, setTableData] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [activeSession, setActiveSession] = useState<Session | null>(null)
  const [balance, setBalance] = useState(0)

  const [user, setUser] = useState<{userId?: string; name?: string; role?: string} | null>(null)

  // Username with fallback
  const username = user?.name || user?.userId || "Guest"
  
  // Header text with balance
  const headerText = `Three Circle 5 - Welcome: ${username} | Balance: ₹${balance}`

  const tableHeaders = [ 
    { label: "#", sortable: true },
    { label: "Date", sortable: true },
    { label: "Session", sortable: true },
    { label: "Numbers", sortable: true },
    { label: "Qty", sortable: true },
    { label: "Amount", sortable: true },
    { label: "Status", sortable: true },
  ]
 // Fetch user data from localStorage and balance from API
  useEffect(() => {
    const loadUserData = async () => {
      try {
        // Get user data from localStorage
        const userData = localStorage.getItem("user_data")
        if (userData) {
          const parsedUser = JSON.parse(userData)
          setUser(parsedUser)
        }

        // Get balance from API
        const token = localStorage.getItem('auth_token')
        if (token) {
          try {
            const balanceResponse = await apiService.getBalance()
            if (balanceResponse.success && balanceResponse.data) {
              setBalance(balanceResponse.data.balance || 0)
            }
          } catch (error) {
            console.error("Error fetching balance:", error)
            setBalance(0)
          }
        }
      } catch (error) {
        console.error("Error loading user data:", error)
      }
    }

    loadUserData()
  }, [])
  // Fetch active session and all sessions
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true)
        
        // Get active session
        const sessionResponse = await apiService.getActiveSession()
        if (sessionResponse.success && sessionResponse.data) {
          setActiveSession(sessionResponse.data)
          setSelectedSession(sessionResponse.data.id)
        }
 const balanceResponse = await apiService.getBalance()
      if (balanceResponse.success && balanceResponse.data) {
        setBalance(balanceResponse.data.balance)
      }
        // Get all sessions
        const sessionsData = await fetchSessions()
        setSessions(sessionsData)
        
        // If no active session but we have sessions, select the first one
        if (!activeSession && sessionsData.length > 0) {
          setSelectedSession(sessionsData[0].id)
        }
      } catch (error) {
        console.error("Error loading initial data:", error)
      } finally {
        setLoading(false)
      }
    }
    
    loadInitialData()
  }, [])

  // Fetch choice list data when session changes
  useEffect(() => {
    const fetchChoiceList = async () => {
      if (!selectedSession) return
      
      setLoading(true)
      try {
        const token = localStorage.getItem('auth_token')
        if (!token) {
          console.error("No auth token found")
          return
        }

        // Fetch choice list with session filter
        const response = await fetch(
          `https://api-dear-lottery.threecircle5.com/api/choice-range/list?sessionId=${selectedSession}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        
        if (data.success && data.choices) {
          // Transform data for table display
          const formattedData = data.choices.map((item: any, index: number) => ({
            id: item.id,
            srNo: index + 1,
            date: new Date(item.createdAt).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            }),
            session: item.sessionId,
            numbers: item.numbers,
            qty: item.qty,
            amount: `₹${item.amount}`,
            status: item.userId ? "✅ Booked" : "⏳ Available",
            rawData: item,
            userName: item.user?.name || "N/A",
            userId: item.userId,
            totalAmount: item.totalAmount
          }))
          
          setTableData(formattedData)
        } else {
          console.error("API response indicates failure:", data)
          setTableData([])
        }
      } catch (error) {
        console.error("Error fetching choice list:", error)
        setTableData([])
      } finally {
        setLoading(false)
      }
    }

    fetchChoiceList()
  }, [selectedSession])

  const handleSessionChange = (sessionId: string) => {
    setSelectedSession(sessionId)
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Mobile Header - Fixed for better mobile experience */}
      <header className="bg-gradient-to-r from-red-800 to-red-900 text-white py-3 px-3 sm:px-4 flex items-center justify-between shadow-md fixed top-0 left-0 right-0 z-50">
        <div className="flex items-center gap-4">
          <span className={cn("text-lg font-semibold", isMobileMenuOpen && "hidden sm:block")}>
            {headerText}
          </span>
        </div>
        <NavigationMenu isMobileMenuOpen={isMobileMenuOpen} setIsMobileMenuOpen={setIsMobileMenuOpen} />
      </header>

      {/* Add padding top to account for fixed header */}
      <div className="pt-16">
        <ScrollingAnnouncementBar text="🎯 Welcome to Three Circle 7 • Play Responsibly • Good Luck! •" />

        <main className="p-2 sm:p-3">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Session Selector - Mobile Optimized */}
            <div className="p-3 border-b border-gray-200">
              <div className="flex flex-col space-y-2">
                <label htmlFor="session-select" className="block text-sm font-medium text-gray-700">
                  Select Session
                </label>
                <select
                  id="session-select"
                  value={selectedSession}
                  onChange={(e) => handleSessionChange(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  disabled={loading}
                >
                  <option value="">Select a session</option>
                  {sessions.map((session) => (
                    <option key={session.id} value={session.id} className="text-sm">
                      {session.name} - {new Date(session.date).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short'
                      })} ({session.drawTime})
                      {session.active ? ' 🟢' : ' 🔴'}
                    </option>
                  ))}
                </select>
                {loading && (
                  <div className="flex items-center justify-center py-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
                    <span className="text-xs text-gray-500">Loading...</span>
                  </div>
                )}
              </div>
            </div>

            {/* Game Info Section - Mobile Optimized */}
            <div className="p-3">
              <GameInfoSection 
                selectedGame={selectedGame} 
                onGameChange={setSelectedGame}
                activeSession={activeSession}
                sessions={sessions}
                selectedSession={selectedSession}
                onSessionChange={handleSessionChange}
                showSessionFilter={true}
              />
            </div>

            {/* Choice List Section */}
            <div className="p-3 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 space-y-2 sm:space-y-0">
                <h2 className="text-lg font-bold text-gray-800">Choice List</h2>
                {selectedSession && sessions.length > 0 && (
                  <div className="text-xs sm:text-sm text-gray-600 bg-gray-50 px-3 py-1 rounded-full">
                    Session: <span className="font-semibold text-red-600">
                      {sessions.find(s => s.id === selectedSession)?.name || selectedSession}
                    </span>
                  </div>
                )}
              </div>
              
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-flex flex-col items-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mb-2"></div>
                    <p className="text-sm text-gray-500">Loading choice list...</p>
                  </div>
                </div>
              ) : tableData.length > 0 ? (
                <>
                  {/* Mobile-friendly summary stats */}
                  <div className="grid grid-cols-3 gap-2 mb-4 sm:hidden">
                    <div className="bg-blue-50 p-2 rounded text-center">
                      <p className="text-xs text-gray-500">Total Items</p>
                      <p className="font-bold text-blue-700">{tableData.length}</p>
                    </div>
                    <div className="bg-green-50 p-2 rounded text-center">
                      <p className="text-xs text-gray-500">Booked</p>
                      <p className="font-bold text-green-700">
                        {tableData.filter(item => item.rawData.userId).length}
                      </p>
                    </div>
                    <div className="bg-yellow-50 p-2 rounded text-center">
                      <p className="text-xs text-gray-500">Available</p>
                      <p className="font-bold text-yellow-700">
                        {tableData.filter(item => !item.rawData.userId).length}
                      </p>
                    </div>
                  </div>

                  {/* Desktop Table / Mobile Cards */}
                  <div className="hidden sm:block">
                    <GenericTablePageContent
                      title=""
                      tableHeaders={tableHeaders}
                      showGroupFilter={false}
                      defaultStartDate={new Date()}
                      defaultEndDate={new Date()}
                      renderRowCells={renderListRowCells}
                      data={tableData}
                    />
                  </div>

                  {/* Mobile Cards View */}
                  <div className="sm:hidden space-y-3">
                    {tableData.map((item) => (
                      <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded">
                              #{item.srNo}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded ${item.status.includes('Booked') ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {item.status}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">{item.date}</span>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Numbers:</span>
                            <span className="text-sm font-semibold text-gray-800">{item.numbers}</span>
                          </div>
                          
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Quantity:</span>
                            <span className="text-sm font-semibold">{item.qty}</span>
                          </div>
                          
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Amount:</span>
                            <span className="text-sm font-bold text-red-600">{item.amount}</span>
                          </div>
                          
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Session:</span>
                            <span className="text-sm text-gray-800">ID: {item.session}</span>
                          </div>
                          
                          {item.userName && item.userName !== "N/A" && (
                            <div className="flex justify-between pt-2 border-t border-gray-100">
                              <span className="text-xs text-gray-500">Booked by:</span>
                              <span className="text-xs font-medium text-gray-700">{item.userName}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Mobile pagination/controls */}
                  <div className="sm:hidden flex justify-between items-center mt-4 pt-3 border-t border-gray-200">
                    <button className="text-sm text-red-600 font-medium px-3 py-1 border border-red-200 rounded-lg">
                      ← Previous
                    </button>
                    <span className="text-sm text-gray-600">
                      Page 1 of {Math.ceil(tableData.length / 10)}
                    </span>
                    <button className="text-sm text-red-600 font-medium px-3 py-1 border border-red-200 rounded-lg">
                      Next →
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="inline-flex flex-col items-center">
                    <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-gray-500 text-sm">No choice list data available</p>
                    <p className="text-gray-400 text-xs mt-1">Select a different session or try again later</p>
                  </div>
                </div>
              )}
            </div>
          </div>

        </main>
      </div>
    </div>
  )
}